import { contourHierarchy, lp } from '../../util/bubbletreemap';
// import { extractScaleFactor } from '../../EntityFieldLevelGraph/utils';
import { useSelector, useDispatch } from 'react-redux'; // To call Action Creators
import { State } from '../../../../../state';
import { bindActionCreators } from "redux";
import { actionCreators } from '../../../../../state';
import _ from 'lodash';

import { MainViewTypes } from '../../../../../types/data';


interface HighlightedGrantGroupNode {
  grantFieldID: string,
  grantFieldName: string,
}

export const drawBubbleTreemap = (
  // main data
  entityFieldList: any,

  // other data
  updatedEntityFieldData: any[],

  // ui parameters
  selectedImpactTypeList: string[],
  leafNodeSizeScaleType: string, // 'pow', 'log', 'linear',
  isShowLeafNode: boolean,
  groupRefs: React.RefObject<{[key: string]: SVGGElement | null}>,

  // ui interaction
  highlightedNode: HighlightedGrantGroupNode,
  handleMouseRightClick: (e: MouseEvent, id: string) => void,
  handleMouseLeave: (e: MouseEvent, id: string) => void,
  handleMouseEnter: (e: MouseEvent, id: string) => void,
  handleMouseOnClick: (e: MouseEvent, id: string) => void,

  // const parameters
  padding: number,
  curvature: number,

  // helper functions
  extractScaleFactor: (referenceNodeValue: number, referenceNodeR: number, scaleType: string) => number,
  getTransformedValue: (value: number, scaleType: string) => number,

) => {

  // console.log('\n\n ============ drawBubbleTreemap() ============')
  // console.log('==> selectedImpactTypeList: ', selectedImpactTypeList)
  // console.log('==> entityFieldList length: ', entityFieldList?.length || 0)

  // find the root node, as input for the following steps
  const rootNode = entityFieldList.find((node: any) => node.depth === 0)
  if (!rootNode) {
    console.warn('drawBubbleTreemap: No root node found')
    return <></>
  }

  const rootEntityType: string = rootNode.data.name

  // The root should be 'impact-type-entity-root', not a specific entity type
  if (rootEntityType === 'grant') {
    console.log('==> Skipping grant entity type')
    return <></>
  }

  // Validate root structure
  if (rootEntityType !== 'impact-type-entity-root') {
    console.warn(`==> Unexpected root entity type: ${rootEntityType}, expected: impact-type-entity-root`)
  }

  // Ensure root has children
  if (!rootNode.children || rootNode.children.length === 0) {
    console.warn('drawBubbleTreemap: Root node has no children')
    return <></>
  }

  // console.log('==> Root children count:', rootNode.children.length)
  // console.log('==> Root children names:', rootNode.children.map((c: any) => c.data.name))

  // ====== 1. calc data for drawing (start) ======
  // 1-1 calc leaf nodes positions (bubble treemap function)
  const hierarchyRoot_updated = lp(rootNode, padding)
  const leafNodes: any[] = hierarchyRoot_updated.descendants().filter((node: any) => !node.children)
  // post process: add parents (depth === 1)
  const leafNodeDataList: any = leafNodes.map((nodeData) => {
    // recursively to find parent node (with depth === 1) name and depth
    let currentNode = nodeData
    while (currentNode.parent && currentNode.parent.depth !== 1) {
      currentNode = currentNode.parent;
    }
    let parentNodeData: any = {}
    if (currentNode.parent) {
        parentNodeData = {
            name: currentNode.parent.data.name,
            type: currentNode.parent.data.type,
            depth: currentNode.parent.depth, // depth === 1 is the entity type level
        }
    } else {
        parentNodeData = {
            name: 'root\'s root',
            type: '',
            depth: -1, // depth === 1 is the entity type level
        }
    }
    return {
      x: nodeData.x,
      y: nodeData.y,
      r: nodeData.r,
      data: {
        name: nodeData.data.name,
        value: nodeData.data.value,
      },
      'parent_entity_type': parentNodeData.name,
    }

  })
  // calc leaf node size scale, used for highlight a grant field node
  /*
      select one impact type, and the first leaf node in this entity type, as input for calc leaf node size scale.
      note: other impact types and leaf nodes should result in the same scale.
  */
  // console.log('selectedImpactTypeList: ', selectedImpactTypeList)
  // console.log('leafNodeDataList: ', leafNodeDataList)
  // console.log('leafNodeDataList.map(n => n.parent_entity_type): ', leafNodeDataList.map((n: any) => n.parent_entity_type))

  const exampleImpactType = selectedImpactTypeList[0]
  const leafNodeList_ImpactType = leafNodeDataList.filter((node: any) => node.parent_entity_type === exampleImpactType)
  if (leafNodeList_ImpactType.length === 0) {
    console.error(`No leaf nodes found for impact type: ${exampleImpactType}`)
    console.error('Available parent_entity_types:', Array.from(new Set(leafNodeDataList.map((n: any) => n.parent_entity_type))))
    return <></>
  }
  const exampleNode = leafNodeList_ImpactType[0]
  const value = exampleNode.data.value
  const r = exampleNode.r
  const leafNodeSizeScale = extractScaleFactor(value, r, leafNodeSizeScaleType)

  // 1-2 calc contour paths (bubble treemap function)
  const contourListData = contourHierarchy(hierarchyRoot_updated, padding, curvature)


  // ====== 2. draw JSX elements (start) ======
  // 2-1 draw leaf nodes and contour
  /*
      input: `leafNodeDataList`, `contourListData`
      method: first, group elements (leaf nodes + contour) by entity type groups; second, draw entity type group one by one
  */
  // for each entity type, draw leaf nodes and contour:
  const bubbleTreemapEntityGroupList: MainViewTypes.BubbleTreemapEntityTypeGroup[] = []
  const bubbleTreemapGeometryGroup: MainViewTypes.BubbleTreemapGeometryGroup[] = []
  const entityList = selectedImpactTypeList
  _.forEach(entityList, (impactType, idx) => {
    if (selectedImpactTypeList.includes(impactType)) {
      // filter element in this group
      const leafNodeList_ImpactType = leafNodeDataList.filter((node: any) => node.parent_entity_type === impactType)
      const contourList_ImpactType = contourListData.filter(contour => contour.parent_entity_type === impactType)

      // console.log('leafNodeList_ImpactType: ', leafNodeList_ImpactType)
      // console.log('contourList_ImpactType: ', contourList_ImpactType)

      // find entity type force-directed graph node position
      const entityTypeGraph_NodePosition = updatedEntityFieldData.find(entity => entity.name === impactType)

      // find rootNode's children - this entity's r
      const entityTypeEntity = rootNode.children.find((node: any) => node.data.name === impactType)

      // generate JSX Element
      if ((leafNodeList_ImpactType.length > 0) && (contourList_ImpactType.length > 0) && (entityTypeGraph_NodePosition)){
        // (1) draw leaf node
        const leafNodeElementList: JSX.Element[] = leafNodeList_ImpactType.map((node: any, idx: number) => {
          // set text label
          let textInfo: string = node.data.name
          if (impactType === 'newsfeed' || impactType === 'policy') {
            // pass
          } else if (impactType === 'patent') {
            // textInfo = cpcCode2Name[node.data.name]
          } else if (impactType === 'clinical_trial') {
            // textInfo = meshCode2Name[node.data.name]
          } else if (impactType === 'paper') {
            // pass
          } else {
            console.log('error: unknown impact type')
          }

          // set fill color
          let fill = "url(#leaf-node-fill)"
          // console.log('highlghtedGrantFieldNode: ', highlghtedGrantFieldNode)
          if (highlightedNode.grantFieldID === node.data.name && highlightedNode.grantFieldName === impactType) {
            fill = 'url(#leaf-node-fill-predict)'
          } else if (highlightedNode.grantFieldID === '' && highlightedNode.grantFieldName === '') {
            fill = "url(#leaf-node-fill)"
          } else {
            fill = 'none'
          }

          let opacity = 1
          if (isShowLeafNode === false) {
            opacity = 0
            // if (highlghtedGrantFieldNode.grantFieldID === '') {
            //   opacity = 1
            // }
          }

          return (
            <g key={`leaf-node-${node.data.name}-${impactType}`} id={`leaf-node-${node.data.name}-${impactType}`}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  // console.log('hover!!!!!')
                  const id = e.currentTarget.id
                  handleMouseEnter(e as unknown as MouseEvent, id)
                }}
                onMouseLeave={(e) => {
                  // console.log('leave!!!!!')
                  const id = e.currentTarget.id
                  handleMouseLeave(e as unknown as MouseEvent, id)
                }}
                onClick={(e) => {
                  // console.log('down!!!!!')
                  const id = e.currentTarget.id
                  handleMouseOnClick(e as unknown as MouseEvent, id)
                }}
            >
              <circle id={`leaf-node-r={${node.r}}-value={${node.data.value}}-${node.data.name}-${impactType}`}
                cx={node.x} cy={node.y} r={Math.pow(node.r, 1)} // r={node.r}
                // fill="#6F999B"
                // fill={node.depth === 0 ? "red" : node.children ? "grey" : "steelblue"}
                // filter="url(#frosted-glass-effect)"
                // fill="url(#leaf-node-filter-2)"
                // fill="url(#leaf-node-fill)"
                fill={fill}
                opacity={ opacity }
                // opacity={ (isShowLeafNode) ? 1 : 0}
                // stroke="transparent" strokeWidth={4}
              />
              <text x={node.x} y={node.y} dy=".35em" textAnchor="middle" fill="white" fontSize={14}>
                {textInfo}
              </text>
            </g>
          )
        })
        // (2) draw contour
        let fill = "url(#counter-node-fill)"
        if (highlightedNode.grantFieldName === impactType) {
          fill = 'url(#counter-node-fill-predict)'
        } else if (highlightedNode.grantFieldID === '' && highlightedNode.grantFieldName === '') {
          fill = "url(#counter-node-fill)"
        } else {
          fill = 'none'
        }
        const contourElementList: JSX.Element[] = contourList_ImpactType.map((arc, idx) => (
          <path key={`${idx}`} id={`${idx}`} d={arc.d} transform={arc.transform}
                // filter="url(#frosted-glass-effect)"
                fill={fill}
                // fill="url(#counter-node-fill)"
                fillOpacity={0.5}
                stroke="white" strokeWidth={1}  strokeOpacity={1}
                // fill="#6F999B"
                // fillRule="evenodd"
          />
        ))

        // (3) draw current impact type bubble treemap group
        // set position transformation for each impact type node's text label
        const centerX: number = _.meanBy(leafNodeList_ImpactType, 'x')
        const centerY: number = _.meanBy(leafNodeList_ImpactType, 'y')
        let dx = 0
        let dy = 0
        // let rotate = 0
        // if (impactType === 'patent') {
        //   dx = 150
        //   dy = 0
        // } else if (impactType === 'clinical_trial') {
        //   dx = -80
        //   dy = 120
        // } else if (impactType === 'policy') {
        //   dx = -100
        //   dy = 0
        //   // rotate = 40
        // }

        // update JSX Element
        bubbleTreemapEntityGroupList.push({
          entityType: impactType,
          JSXElement: (
            <g
              ref={(el) => groupRefs.current && (groupRefs.current[impactType] = el)}
              key={impactType}
              id={`bubble-treemap-${impactType}-g`}
              // transform={`translate(${-centerX + entityTypeGraph_NodePosition.x}, ${-centerY + entityTypeGraph_NodePosition.y})`}
              // // transform={`translate(${-centerX + entityTypeGraph_NodePosition.x}, ${-centerY + entityTypeGraph_NodePosition.y}) rotate(${rotate}, ${centerX}, ${centerY})`}

              onContextMenu={(e) => {
                // console.log('right click the context menu: ')
                e.preventDefault()
                const id = e.currentTarget.id
                handleMouseRightClick(e as unknown as MouseEvent, id)
              }}
            >
              {contourElementList}
              {leafNodeElementList}
              <text id={`bubble-treemap-text-label-${impactType}`} transform={`translate(${centerX + dx}, ${centerY + dy})`} fontSize={14} fill="#404040">{impactType}</text>
              {/* debug: 这个entity type的最大的圆 */}
              {/* <circle id={`debug-${entityTypeEntity.data.name}`} cx={entityTypeEntity.x} cy={entityTypeEntity.y} r=
                {entityTypeEntity.r} fill='red' opacity={0.5} /> */}
            </g>
          ),
          center: {
            x: centerX,
            y: centerY,
          }
        })

        // update geometry group
        bubbleTreemapGeometryGroup.push({
          entityType: impactType,
          center: {
            x: centerX,
            y: centerY,
          },
          impactType: impactType,
          contourList_ImpactType_Data: contourList_ImpactType,
          leafNodeList_ImpactType_Data: leafNodeList_ImpactType,
        })
      } else {
        bubbleTreemapEntityGroupList.push(
          {
            entityType: impactType,
            JSXElement: (
              // temp, to update transform with force-directed result
              <g key={impactType} id={impactType}></g>  // empty element group
            ),
            center: {
              x: 0,
              y: 0,
            }
          }
        )
        bubbleTreemapGeometryGroup.push({
          entityType: impactType,
          center: {
            x: 0,
            y: 0,
          },
          impactType: impactType,
          contourList_ImpactType_Data: [],
          leafNodeList_ImpactType_Data: [],
        })
      }
    }
  })

  // 2-2 init edge bundling data (record leaf node positions)
  const leafNodePositions: MainViewTypes.LeafNodePosition[] = []
  leafNodeDataList.forEach((leafNodeData: any) => {
    leafNodePositions.push({
      x: leafNodeData.x,
      y: leafNodeData.y,
      r: leafNodeData.r,
      data: {
        name: leafNodeData.data.name,
        value: leafNodeData.data.value,
        entityType: leafNodeData.parent_entity_type,
      },
    })
  })
  // console.log('leafNodePositions: ', leafNodePositions)

  return {
    bubbleTreemapEntityGroupList,
    bubbleTreemapGeometryGroup,
    leafNodePositions,
    leafNodeSizeScale,
    leafNodeSizeScaleType
  }
}